import React, { useState } from 'react';
import {
    IonCard,
    IonCardHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonGrid,
    IonRow,
    IonCol,
    IonSpinner,
    IonText,
    IonInputOtp
} from '@ionic/react';
import { arrowBack, checkmarkCircle } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';

interface VerificationCodeCardProps {
    email: string;
    isVerifying: boolean;
    onGoBack: () => void;
    onVerifyEmailCode: (code: string) => void;
    onVerifyEmployeeCode: (code: string) => void;
    onResendCode: () => void;
    initialMode?: 'email' | 'employee'; // Add prop to control initial mode
}

export const VerificationCodeCard: React.FC<VerificationCodeCardProps> = ({
    email,
    isVerifying,
    onGoBack,
    onVerifyEmailCode,
    onVerifyEmployeeCode,
    onResendCode,
    initialMode = 'email', // Default to email mode
}) => {
  const { t } = useTranslation();
    const [code, setCode] = useState('');
    const isEmployeeCode = initialMode === 'employee';
    
    const codeLength = isEmployeeCode ? 8 : 6;
    const isEmailVerification = !isEmployeeCode;

    // Typing the code never submits it: only the Verify button (or Enter in the form) does
    const handleOtpChange = (event: CustomEvent) => {
        setCode(event.detail.value || '');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.length === codeLength) {
            if (isEmployeeCode) {
                onVerifyEmployeeCode(code);
            } else {
                onVerifyEmailCode(code);
            }
        }
    };

    return (
        <IonCard className={`${styles.loginCard} ion-text-center`}>
            <IonCardHeader className={styles.cardHeader}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton 
                            fill="clear" 
                            className={styles.backButton}
                            onClick={onGoBack}
                            disabled={isVerifying}
                        >
                            <IonIcon icon={arrowBack} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle className={styles.cardTitle}>
                        {isEmailVerification ? t('components.AuthComponents.VerificationCodeCard.verifyYourEmail') : t('components.AuthComponents.VerificationCodeCard.enterVerificationCode')}
                    </IonTitle>
                </IonToolbar>
            </IonCardHeader>

            <IonGrid>
                <IonRow>
                    <IonCol>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.verificationCodeSection}>
                                <div className={styles.verificationIcon}>
                                    <IonIcon icon={checkmarkCircle} size="large" color="primary" />
                                </div>
                                
                                <IonText className={styles.verificationTitle}>
                                    <h2>
                                        {isEmailVerification 
                                            ? t('components.AuthComponents.VerificationCodeCard.checkYourEmail') 
                                            : t('components.AuthComponents.VerificationCodeCard.enterYourEmployeeCode')
                                        }
                                    </h2>
                                </IonText>
                                
                                <IonText className={styles.verificationDescription}>
                                    <p>
                                        {isEmailVerification 
                                            ? t('components.AuthComponents.VerificationCodeCard.weSentADigitVerificationCode', { codeLength, email })
                                            : t('components.AuthComponents.VerificationCodeCard.enterTheCharacterEmployeeCodeYou', { codeLength })
                                        }
                                    </p>
                                </IonText>

                                <IonInputOtp
                                    className={styles.otpInput}
                                    length={codeLength}
                                    type={isEmailVerification ? 'number' : 'text'}
                                    pattern="."
                                    value={code}
                                    onIonInput={handleOtpChange}
                                    disabled={isVerifying}
                                    size='small'
                                    style={{ '--otp-count': codeLength } as React.CSSProperties}
                                />

                                <div className={styles.verificationActions}>
                                    <div className={styles.centerCol}>
                                        <IonButton
                                            expand="block"
                                            type="submit"
                                            disabled={code.length !== codeLength || isVerifying}
                                            className={`${styles.customButton} ${styles.mainButton}`}
                                        >
                                            {isVerifying ? (
                                                <IonSpinner name="dots" />
                                            ) : (
                                                t('components.AuthComponents.VerificationCodeCard.verify', { v1: translateDynamic(isEmailVerification ? 'Email' : 'Code') })
                                            )}
                                        </IonButton>

                                        {isEmailVerification && (
                                            <IonButton
                                                expand="block"
                                                fill="outline"
                                                onClick={onResendCode}
                                                disabled={isVerifying}
                                                className={`${styles.customButton} ion-margin-top`}
                                            >{t('components.AuthComponents.VerificationCodeCard.resendCode')}
                                            </IonButton>
                                        )}
                                    </div>

                                    <IonText className={styles.helpText}>
                                        <p>
                                            {isEmailVerification 
                                                ? t('components.AuthComponents.VerificationCodeCard.didnTReceiveTheCodeCheck')
                                                : t('components.AuthComponents.VerificationCodeCard.contactYourAdministratorIfYouNeed')
                                            }
                                        </p>
                                    </IonText>
                                </div>
                            </div>
                        </form>
                    </IonCol>
                </IonRow>
            </IonGrid>
        </IonCard>
    );
};
